import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// Helper.
import { getDefaultPageSize, sortStrings } from "../../../helpers/utils";

import { Column, usePagination, useSortBy, useTable } from 'react-table';

import Skeleton from "react-loading-skeleton";

import TableCSVExport from '../../../component/TableCSVExport';

// Context.
import { HostContext } from '../../../context/HostContext';

// Types.
import StatusIcon from '../../StatusIcon';
import HostType from '../HostType';
import HostsDataTable from '../../../types/host/tables/HostsDataTable';

// Components.
import AverageStatistic from "../widget/AverageStatistic";
import PeakStatistic from "../widget/PeakStatistic";
import ReactTable from '../../ReactTable';

// Constants.
import { DATA_LOADING } from "../../Constants";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildRedirectQuery } from "../../../helpers/timeRangeUtils";
import SearchInput from '../../SearchInput';

function HostsTable(props: { loading: number, hostsDataTable: HostsDataTable[] }) {
    const { hosts } = useContext(HostContext);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());
    const timeRangeContext = useContext(TimeRangeContext)
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const data = useMemo(() => props.hostsDataTable.filter(row => (row.name.toLowerCase().includes(tableFilter.toLowerCase()) || row.type.toLowerCase().includes(tableFilter.toLowerCase()))), [props.hostsDataTable, tableFilter]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            Header: 'Host',
            accessor: 'name',
            className: 'fw-bold col-width-40',
            defaultCanSort: true,
            sortType: sortStrings,
            Cell: ({ cell }) => (
                    <React.Fragment>
                        <StatusIcon mode="public" hostId={cell.row.values.id}/>
                        <Link to={`/hosts/${cell.row.values.id}?${buildRedirectQuery(timeRangeContext.timeRange)}`}>{cell.row.values.name}</Link>
                    </React.Fragment>
            )
        },
        {
            Header: '',
            accessor: 'type',
            className: 'text-center col-width-5',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                    <React.Fragment>
                        <HostType host={hosts.filter(host => Number(host.id) === Number(cell.row.values.id))[0]}/>
                    </React.Fragment>
            )
        },
        {
            Header: 'Instances',
            accessor: 'instances',
            headerClassName: 'text-end',
            className: 'text-end col-width-10',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => (
                    cell.row.values.instances !== null ? (
                            cell.row.values.instances.length ? <Link to={`/hosts/${cell.row.values.id}/instances`}>{cell.row.values.instances.length}</Link> : 0

                    ) : (
                            <Skeleton/>
                    )
            )
        },
        {
            Header: 'CPU Average',
            accessor: 'processor',
            headerClassName: 'text-end',
            className: 'text-end col-width-10',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => (
                    cell.row.values.processor !== null ? (
                            <AverageStatistic hostId={cell.row.values.id} statistic="cpuutilisation" metric={cell.row.values.processor} previousMetric={(cell.row.original as HostsDataTable).previousProcessor}/>
                    ) : (
                            <Skeleton/>
                    )
            )
        },
        {
            Header: 'CPU Peak',
            accessor: 'processorPeak',
            headerClassName: 'text-end',
            className: 'text-end col-width-10',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => (
                    cell.row.values.processorPeak !== null ? (
                            <PeakStatistic hostId={cell.row.values.id} statistic="cpuutilisation" metric={cell.row.values.processorPeak} previousMetric={(cell.row.original as HostsDataTable).previousProcessorPeak}/>
                    ) : (
                            <Skeleton/>
                    )
            )
        },
        {
            Header: 'Memory Average',
            accessor: 'memory',
            headerClassName: 'text-end',
            className: 'text-end col-width-10',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => (
                    cell.row.values.memory !== null ? (
                            <AverageStatistic hostId={cell.row.values.id} statistic="memoryutilisation" metric={cell.row.values.memory} previousMetric={(cell.row.original as HostsDataTable).previousMemory}/>
                    ) : (
                            <Skeleton/>
                    )
            )
        },
        {
            Header: 'Disk I/O',
            accessor: 'ioutilisation',
            headerClassName: 'text-end',
            className: 'text-end col-width-10',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => (
                    cell.row.values.ioutilisation !== null ? (
                            <PeakStatistic hostId={cell.row.values.id} statistic="ioutilisation" metric={cell.row.values.ioutilisation} previousMetric={(cell.row.original as HostsDataTable).previousIoutilisation}/>
                    ) : (
                            <Skeleton/>
                    )
            )
        },
    ], [hosts]);

    const tableInstance = useTable({
                columns,
                data,
                initialState: { hiddenColumns: ['id'],
                    pageIndex: initialPageIndex,
                    pageSize: initialPageSize, sortBy: [{ id: 'name', desc: false }] },
                autoResetPage: false,
                autoResetSortBy: false,
                autoResetFilters: false,
                autoResetSelectedRows: false,
                autoResetExpanded: false,
                autoResetGroupBy: false,
            },
            useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    return (
            <React.Fragment>
                <div className="row row-cols-1 row-cols-md-2 table-search">
<div className="col col-md-10">
                    <TableCSVExport
                        tableInstance={tableInstance}
                        filename="Hosts"
                    />
                    </div>
                    <div className="col col-md-2">
                        <SearchInput
                            ref={inputRef}
                            value={tableFilter}
                            onChange={(e) => setTableFilter(e.target.value)}
                            onClear={clearTableFilter}
                        />
                    </div>
                </div>
                <ReactTable
                    getTableProps={getTableProps}
                    getTableBodyProps={getTableBodyProps}
                    headerGroups={headerGroups}
                    prepareRow={prepareRow}
                    page={page}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageOptions={pageOptions}
                    pageCount={pageCount}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    setPageSize={setPageSize}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    dataLength={data.length}
                    error={null}
                    loading={props.loading === DATA_LOADING}
                />
            </React.Fragment>
    );
}

export default HostsTable;